import type { ILandingTemplateButton } from './landingTemplateButtons'
import { CustomModuleBaseTypeEnum } from '~/shared/api'
import { ProjectModuleSlugEnum } from '~/shared/const'
import { WORKSPACE_MODULE_COLORS } from '~/shared/const/workspaceModuleUi'
import {
  isLandingPlacementButtonConfigured,
  LANDING_NATIVE_MODULE_CATALOG,
} from './landingTemplateButtons'
import { normalizeWorkspaceModuleSlug } from './workspaceModuleVisibility'

const NATIVE_PLACEMENT_MODULE_TYPE: Partial<Record<string, CustomModuleBaseTypeEnum>> = {
  [ProjectModuleSlugEnum.ProjectNews]: CustomModuleBaseTypeEnum.News,
  [ProjectModuleSlugEnum.ProjectSpeeches]: CustomModuleBaseTypeEnum.News,
  [ProjectModuleSlugEnum.ProjectSurvey]: CustomModuleBaseTypeEnum.Survey,
  [ProjectModuleSlugEnum.Supporters]: CustomModuleBaseTypeEnum.Supporters,
  [ProjectModuleSlugEnum.ProjectOrgMail]: CustomModuleBaseTypeEnum.Mail,
}

export function resolveLandingPlacementModuleType(
  moduleSlug: string,
  explicitModuleType?: CustomModuleBaseTypeEnum | null,
): CustomModuleBaseTypeEnum | undefined {
  if (explicitModuleType) {
    return explicitModuleType
  }

  return NATIVE_PLACEMENT_MODULE_TYPE[normalizeWorkspaceModuleSlug(moduleSlug)]
}

export type LandingProjectPlacementButtonsMap = Record<string, ILandingTemplateButton>

function isLandingPlacementButton(value: unknown): value is ILandingTemplateButton {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as Partial<ILandingTemplateButton>
  return typeof candidate.moduleSlug === 'string'
    && typeof candidate.label === 'string'
    && typeof candidate.color === 'string'
}

export function parseLandingProjectPlacementButtons(
  raw: unknown,
): LandingProjectPlacementButtonsMap {
  if (!raw || typeof raw !== 'object') {
    return {}
  }

  if (Array.isArray(raw)) {
    const map: LandingProjectPlacementButtonsMap = {}
    for (const item of raw) {
      if (!isLandingPlacementButton(item)) {
        continue
      }
      const slug = normalizeWorkspaceModuleSlug(item.moduleSlug)
      map[slug] = {
        ...item,
        moduleSlug: slug,
        moduleObjectId: item.moduleObjectId ?? null,
      }
    }
    return map
  }

  const map: LandingProjectPlacementButtonsMap = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!isLandingPlacementButton(value)) {
      continue
    }
    const slug = normalizeWorkspaceModuleSlug(value.moduleSlug || key)
    map[slug] = {
      ...value,
      moduleSlug: slug,
      moduleObjectId: value.moduleObjectId ?? null,
    }
  }

  return map
}

export function serializeLandingProjectPlacementButtons(
  map: LandingProjectPlacementButtonsMap,
): Record<string, ILandingTemplateButton> {
  const serialized: Record<string, ILandingTemplateButton> = {}

  for (const [slug, button] of Object.entries(map)) {
    const normalizedSlug = normalizeWorkspaceModuleSlug(slug)
    serialized[normalizedSlug] = {
      moduleSlug: normalizedSlug,
      label: button.label,
      color: button.color,
      ...(button.moduleObjectId
        ? { moduleObjectId: button.moduleObjectId }
        : {}),
      ...(button.moduleType
        ? { moduleType: button.moduleType }
        : {}),
    }
  }

  return serialized
}

export function listLandingProjectPlacementButtons(
  map: LandingProjectPlacementButtonsMap,
): ILandingTemplateButton[] {
  return Object.values(map)
}

export function listConfiguredLandingProjectPlacementButtons(
  map: LandingProjectPlacementButtonsMap,
): ILandingTemplateButton[] {
  return listLandingProjectPlacementButtons(map).filter(isLandingPlacementButtonConfigured)
}

export function hasLandingProjectPlacementButton(
  map: LandingProjectPlacementButtonsMap,
  moduleSlug: string,
): boolean {
  const slug = normalizeWorkspaceModuleSlug(moduleSlug)
  const button = map[slug]
  return Boolean(button && isLandingPlacementButtonConfigured(button))
}

export function buildDefaultLandingProjectPlacementButton(
  moduleSlug: string,
  options?: {
    label?: string
    color?: string
    moduleType?: ILandingTemplateButton['moduleType']
  },
): ILandingTemplateButton {
  const normalizedSlug = normalizeWorkspaceModuleSlug(moduleSlug)
  const catalogMeta = LANDING_NATIVE_MODULE_CATALOG[normalizedSlug]
  const moduleType = resolveLandingPlacementModuleType(normalizedSlug, options?.moduleType)

  return {
    moduleSlug: normalizedSlug,
    label: options?.label?.trim() || catalogMeta?.title || normalizedSlug,
    color: options?.color?.trim() || WORKSPACE_MODULE_COLORS[0],
    ...(moduleType ? { moduleType } : {}),
  }
}

export function setLandingProjectPlacementButton(
  map: LandingProjectPlacementButtonsMap,
  moduleSlug: string,
  button: ILandingTemplateButton | null,
): LandingProjectPlacementButtonsMap {
  const normalizedSlug = normalizeWorkspaceModuleSlug(moduleSlug)
  const next = { ...map }

  if (!button) {
    delete next[normalizedSlug]
    return next
  }

  next[normalizedSlug] = {
    ...button,
    moduleSlug: normalizedSlug,
  }

  return next
}
